import { z } from "zod";

// TES-281: the shared role vocabulary for every authenticated principal.
// Single source of truth — the API's JWT `role` claim, the Employee.role
// column, and the admin SPA's session gate all import from here so the
// union can never drift between surfaces.
//
// `patient` is an auth role but NOT an employee role: patients authenticate
// via the mobile app and can never establish an admin session. The employee
// roles mirror the Ella platform spec §19; the permission *matrix* (which
// role may do what) is deliberately not encoded here — endpoints declare
// their allowed roles via the API's @Roles() decorator.
// Staff roles — everything that may hold an admin session cookie. Kept as a
// literal tuple (not a filter over ROLES) so z.enum and the derived types
// stay narrow.
export const EMPLOYEE_ROLES = ["doctor", "coach", "admin"] as const;

export const ROLES = ["patient", ...EMPLOYEE_ROLES] as const;

// TES-490 — the administrative roles, as one group. Ö-56:
// ONE admin role. `medical_admin` and `content_admin` had lived in the
// vocabulary since TES-281 without a single distinct permission and were
// treated as aliases of `admin` until the answer came; they are gone now.
// The group stays so `@Roles(...ADMIN_ROLES)` / `isAdminRole()` remain the
// one place to widen if a second administrative role ever earns its own
// grants.
export const ADMIN_ROLES = [
  "admin",
] as const satisfies readonly EmployeeRoleName[];

type EmployeeRoleName = (typeof EMPLOYEE_ROLES)[number];

export function isAdminRole(role: unknown): boolean {
  return (
    typeof role === "string" &&
    (ADMIN_ROLES as readonly string[]).includes(role)
  );
}

// TES-1089 — roles an employee may hold ON TOP of her base role
// (Employee.grantedRoles). Simon 2026-10-03: Emelie is coach + admin so she
// sees the statistics. Only `admin` is grantable, and a doctor holds no grant
// at all: the doctor role carries clinical and PDL meaning that must not be
// mixed with another. The DB CHECK employees_granted_roles_check enforces the
// same two rules.
export const GRANTABLE_ROLES = ["admin"] as const;
export type GrantableRole = (typeof GRANTABLE_ROLES)[number];

export function isGrantableRole(role: unknown): role is GrantableRole {
  return (
    typeof role === "string" &&
    (GRANTABLE_ROLES as readonly string[]).includes(role)
  );
}

/**
 * The roles an employee acts with: her base role plus every valid grant.
 * A grant outside GRANTABLE_ROLES, or any grant on a doctor, is ignored, so
 * a row that drifted past the CHECK can never widen access.
 */
export function effectiveRoles(employee: {
  role: string;
  grantedRoles?: readonly string[] | null;
}): EmployeeRoleName[] {
  if (!isEmployeeRole(employee.role)) return [];
  const out: EmployeeRoleName[] = [employee.role];
  if (employee.role === "doctor") return out;
  for (const granted of employee.grantedRoles ?? []) {
    if (isGrantableRole(granted) && !out.includes(granted)) out.push(granted);
  }
  return out;
}

// TES-1372 — purpose-bound permissions, held on Employee.permissions and
// separate from roles. Emelie 2026-10-15 (Ö-546): "Vyn ska kräva en separat
// ändamålsstyrd behörighet, exempelvis ”Insikter”. Vanlig admin- eller
// coachbehörighet ska inte automatiskt ge tillgång." No role implies a
// permission, and a permission widens no role: it opens exactly the routes
// that require it. The DB CHECK employees_permissions_check holds the same
// vocabulary.
//
// TES-1400 (Ö-541, Emelie 2026-10-15) — `deviating_medication`: "Annat
// (avvikande) i läkemedelskatalogen kräver särskild behörighet och
// obligatorisk motivering." An order outside the clinically approved
// catalogue is refused without it (resolveCatalogueLink).
export const STAFF_PERMISSIONS = ["insights", "deviating_medication"] as const;
export type StaffPermission = (typeof STAFF_PERMISSIONS)[number];

export function isStaffPermission(p: unknown): p is StaffPermission {
  return (
    typeof p === "string" &&
    (STAFF_PERMISSIONS as readonly string[]).includes(p)
  );
}

/**
 * The permissions an employee acts with. A value outside STAFF_PERMISSIONS
 * is ignored, so a row that drifted past the CHECK can never open a route.
 */
export function effectivePermissions(employee: {
  role: string;
  permissions?: readonly string[] | null;
}): StaffPermission[] {
  if (!isEmployeeRole(employee.role)) return [];
  return [...new Set((employee.permissions ?? []).filter(isStaffPermission))];
}

export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

export const employeeRoleSchema = z.enum(EMPLOYEE_ROLES);
export type EmployeeRole = z.infer<typeof employeeRoleSchema>;

export function isEmployeeRole(role: unknown): role is EmployeeRole {
  return (
    typeof role === "string" &&
    (EMPLOYEE_ROLES as readonly string[]).includes(role)
  );
}

// Shape of GET employee-api/v1/me — the claims the API echoes back after
// verifying the httpOnly session cookie. Replaces the previously duplicated
// inline unions in apps/api and apps/admin. (Named to avoid colliding with
// the patient-side meResponseSchema in ./patient.)
export const clinicianMeResponseSchema = z.object({
  sub: z.string(),
  tenantId: z.string(),
  role: roleSchema,
  partnerClinicId: z.string().nullable(),
  exp: z.number(),
  // Who is signed in. The endpoint echoed only JWT claims, so the portal
  // could never name its own user — the shell said "Inloggad / Läkare" and
  // a clinician with two accounts (or a shared machine) had no way to tell
  // which one was acting. Every decision the portal records is attributed to
  // this person, so the portal has to be able to show them.
  //
  // Optional: an older API deployment omits it, and the row can genuinely
  // carry no name.
  displayName: z.string().nullable().optional(),
  // TES-1089 — every role the employee acts with right now: `role` plus her
  // grants (effectiveRoles), read from the Employee row on this request, not
  // from the token. `role` stays the base role for backward compatibility.
  // Optional: an older API deployment omits it, and then `[role]` is meant.
  roles: z.array(roleSchema).optional(),
  // TES-1372 — her purpose-bound permissions, read the same way as `roles`.
  // Optional: an older API deployment omits it, and then none is meant.
  permissions: z.array(z.enum(STAFF_PERMISSIONS)).optional(),
});
export type ClinicianMeResponse = z.infer<typeof clinicianMeResponseSchema>;
